/**
 * Chalkboard design tokens — single source of truth for color, typography,
 * and spacing across the AOT public site and product UI.
 *
 * Spec: docs/design/chalkboard.md (sections 3, 4, 6)
 * AIMEAT canonical: shared:design:chalkboard-guideline
 *
 * Usage:
 *   import { color, typo, space, type ChalkboardMode } from '@/styles/chalkboardTokens';
 *   <h1 className={typo.dark.h1}>Title</h1>
 *
 * Design constraints (section 7 don'ts):
 *   - Never introduce a new color outside the table here
 *   - Headings always use font-serif via the typo tokens
 *   - Containers use the named max-width tokens, never raw values
 */

export type ChalkboardMode = 'dark' | 'light';

// ---------------------------------------------------------------------------
// Section 3 — Color tokens
// Each token resolves to a Tailwind class string (text-, bg-, or border-)
// chosen by the consumer's purpose. Two slots: dark (chalkboard) and light
// (paper). The hex in each comment is the canonical Tailwind color used.
// ---------------------------------------------------------------------------

export const color = {
  /**
   * Theme-responsive slot: light values by default, dark values under a
   * `.dark` ancestor (class-based dark mode, see index.css @custom-variant).
   * Use on surfaces that follow the route/user theme (participant browsing
   * pages). Renders identically to `color.dark` when `.dark` is present, so
   * shared components can switch to `auto` without changing dark surfaces.
   * Keep every class literal here — Tailwind extracts them from this table.
   */
  auto: {
    bg: 'bg-stone-50 dark:bg-slate-950',
    surface: 'bg-white/80 dark:bg-slate-900/60',
    borderSubtle: 'border-stone-200 dark:border-slate-800/60',
    textPrimary: 'text-slate-900 dark:text-slate-100',
    textMuted: 'text-slate-600 dark:text-slate-400',
    textFaint: 'text-stone-500 dark:text-slate-500',
    accentSoft: 'text-emerald-700/80 dark:text-emerald-300/80',
    accentSoftBg: 'bg-emerald-700/10 dark:bg-emerald-300/10',
    accentStrong: 'bg-emerald-600 dark:bg-emerald-500',
    accentStrongHover: 'hover:bg-emerald-700 dark:hover:bg-emerald-400',
    accentStrongText: 'text-emerald-700 dark:text-emerald-400',
    error: 'text-red-600 dark:text-red-400',
    errorBg: 'bg-red-50 dark:bg-red-500/10',
    success: 'text-emerald-700 dark:text-emerald-300',
  },
  dark: {
    bg: 'bg-slate-950',                    // #020617
    surface: 'bg-slate-900/60',
    borderSubtle: 'border-slate-800/60',
    textPrimary: 'text-slate-100',         // #f1f5f9
    textMuted: 'text-slate-400',
    textFaint: 'text-slate-500',
    accentSoft: 'text-emerald-300/80',
    accentSoftBg: 'bg-emerald-300/10',
    accentStrong: 'bg-emerald-500',
    accentStrongHover: 'hover:bg-emerald-400',
    accentStrongText: 'text-emerald-400',
    error: 'text-red-400',
    errorBg: 'bg-red-500/10',
    success: 'text-emerald-300',
  },
  light: {
    bg: 'bg-stone-50',                     // #fafaf9
    surface: 'bg-white/80',
    borderSubtle: 'border-stone-200',
    textPrimary: 'text-slate-900',         // #0f172a
    textMuted: 'text-slate-600',
    textFaint: 'text-stone-500',
    accentSoft: 'text-emerald-700/80',
    accentSoftBg: 'bg-emerald-700/10',
    accentStrong: 'bg-emerald-600',
    accentStrongHover: 'hover:bg-emerald-700',
    accentStrongText: 'text-emerald-700',
    error: 'text-red-600',
    errorBg: 'bg-red-50',
    success: 'text-emerald-700',
  },
} as const;

// ---------------------------------------------------------------------------
// Section 4 — Typography tokens
// Composed Tailwind class strings, one entry per typography role. Mode-keyed
// so the mapped text color comes from §3 above (no duplication of slate/stone
// strings — change once in `color`, all typography updates).
// ---------------------------------------------------------------------------

const buildTypo = (mode: ChalkboardMode | 'auto') => {
  const c = color[mode];
  return {
    /** Small uppercase eyebrow above headings or sections */
    label: `text-xs uppercase tracking-[0.2em] ${c.textFaint} font-semibold`,
    /** Hero / page-opening title (largest serif step) */
    h1Display: `font-serif text-4xl md:text-5xl ${c.textPrimary} leading-tight`,
    /** Section title */
    h1: `font-serif text-3xl md:text-4xl ${c.textPrimary} leading-tight`,
    /** Subsection title */
    h2: `font-serif text-2xl md:text-3xl ${c.textPrimary} leading-snug`,
    /** Card title */
    h3: `font-serif text-xl md:text-2xl ${c.textPrimary} leading-snug`,
    /** Body paragraph (default reading copy) */
    body: `font-sans text-base md:text-lg ${c.textPrimary} leading-relaxed`,
    /** Secondary body copy (notes, helper text) */
    bodyMuted: `font-sans text-sm md:text-base ${c.textMuted} leading-relaxed`,
    /** Italic accent capsule (e.g. capsule-summary phrase) */
    capsule: `font-serif italic text-base ${c.accentSoft} tracking-wide`,
    /** Micro footer / caption text */
    caption: `text-xs ${c.textFaint}`,
  } as const;
};

export const typo = {
  /** Theme-responsive (see color.auto) — light by default, dark under `.dark`. */
  auto: buildTypo('auto'),
  dark: buildTypo('dark'),
  light: buildTypo('light'),
} as const;

export type TypoToken = keyof ReturnType<typeof buildTypo>;

// ---------------------------------------------------------------------------
// Section 5 — Cluster colour tokens (data-viz extension to spec §3)
//
// Used wherever multiple participant clusters need to be visually distinct:
// TopicCloud cards, Insight Map dots, polar-map participant rings, etc.
//
// Values are PLACEHOLDERS today — the chalkboard spec does not yet define a
// data-viz palette. When that decision is made, swap the hex/Tailwind values
// here in one place; every consumer updates automatically.
// ---------------------------------------------------------------------------

export interface ClusterColor {
  /** Hex used for SVG fills, dot backgrounds, logo halos */
  hex: string;
  /** Tailwind background utility (cards, badges) */
  bg: string;
  /** Tailwind border utility (card outline, dot ring) */
  border: string;
  /** Tailwind text utility (cluster-name labels) */
  text: string;
  /** Symbolic shape — secondary cue when colour alone is not enough */
  shape: string;
}

// Palette ALIGNED with CLUSTER_COLORS in KeyInsightsPanel.tsx, AllResponsesDisplay.tsx,
// ClusterDetailsPanel.tsx, WordComparisonCard.tsx. Those
// files render the "Available perspectives" pills (Fleshy Vessels / Absent Presences /
// Existential Boundaries …) using this same hex sequence, so the synthesis-level pie
// chart segments now match the pill colour for every cluster.
//
// Follow-up: collapse the 5+ duplicate CLUSTER_COLORS arrays into this single source
// of truth in a separate small PR — high risk of drift otherwise.
export const clusterColor: ReadonlyArray<ClusterColor> = [
  { hex: '#34d399', bg: 'bg-emerald-400', border: 'border-emerald-400', text: 'text-emerald-700 dark:text-emerald-300', shape: '●' },
  { hex: '#fbbf24', bg: 'bg-amber-400',   border: 'border-amber-400',   text: 'text-amber-700 dark:text-amber-300',   shape: '■' },
  { hex: '#f472b6', bg: 'bg-pink-400',    border: 'border-pink-400',    text: 'text-pink-700 dark:text-pink-300',    shape: '▲' },
  { hex: '#60a5fa', bg: 'bg-sky-400',     border: 'border-sky-400',     text: 'text-sky-700 dark:text-sky-300',     shape: '★' },
  { hex: '#a78bfa', bg: 'bg-violet-400',  border: 'border-violet-400',  text: 'text-violet-700 dark:text-violet-300',  shape: '◆' },
  { hex: '#fb923c', bg: 'bg-orange-400',  border: 'border-orange-400',  text: 'text-orange-700 dark:text-orange-300',  shape: '⬡' },
] as const;

/** Resolve a cluster's colour set by 0-based cluster index (cycles for >6 clusters). */
export function clusterColorAt(index: number): ClusterColor {
  return clusterColor[index % clusterColor.length];
}

// ---------------------------------------------------------------------------
// Section 5b — Reflection-dimension colour tokens
//
// Five fixed reflection dimensions (per polarScalesConfig + per-response V2
// "Reflection Fingerprint"). Each dimension has a stable identity colour that
// is shared across the per-response Fingerprint and the synthesis Insight Map
// so a participant can move between the two views without losing the visual
// thread.
//
// Hex values match `ANGLE_COLORS` in components/per-response-v2/reflectionAngles.ts;
// keep them aligned. When the chalkboard spec defines a canonical data-viz
// palette, swap both files to reference these tokens directly.
// ---------------------------------------------------------------------------

export type DimensionKey =
  | 'reflective_stance'
  | 'orientation'
  | 'register'
  | 'coherence'
  | 'temporality'
  | 'expression';

export interface DimensionColor {
  hex: string;
  /** Tailwind background utility for active state */
  bgActive: string;
  /** Tailwind background utility for hover/idle pill */
  bgIdle: string;
  /** Tailwind border utility */
  border: string;
  /** Tailwind text utility */
  text: string;
}

export const dimensionColor: Record<DimensionKey, DimensionColor> = {
  reflective_stance: { hex: '#00BCD4', bgActive: 'bg-cyan-600',    bgIdle: 'bg-cyan-500/10',    border: 'border-cyan-500',    text: 'text-cyan-700 dark:text-cyan-200' },
  orientation:       { hex: '#6366F1', bgActive: 'bg-indigo-600',  bgIdle: 'bg-indigo-500/10',  border: 'border-indigo-500',  text: 'text-indigo-700 dark:text-indigo-200' },
  register:          { hex: '#FF8C00', bgActive: 'bg-orange-600',  bgIdle: 'bg-orange-500/10',  border: 'border-orange-500',  text: 'text-orange-700 dark:text-orange-200' },
  coherence:         { hex: '#A855F7', bgActive: 'bg-purple-600',  bgIdle: 'bg-purple-500/10',  border: 'border-purple-500',  text: 'text-purple-700 dark:text-purple-200' },
  temporality:       { hex: '#F43F5E', bgActive: 'bg-rose-600',    bgIdle: 'bg-rose-500/10',    border: 'border-rose-500',    text: 'text-rose-700 dark:text-rose-200' },
  expression:        { hex: '#1DB954', bgActive: 'bg-emerald-600', bgIdle: 'bg-emerald-500/10', border: 'border-emerald-500', text: 'text-emerald-700 dark:text-emerald-200' },
} as const;

/** Resolve dimension colour by either underscore key or the hyphen variant used by some buttons. */
export function dimensionColorOf(key: string): DimensionColor | undefined {
  const normalised = key.replace(/-/g, '_') as DimensionKey;
  return dimensionColor[normalised];
}

// ---------------------------------------------------------------------------
// Section 5c — Story warm paper (spec §3b; AIMEAT guideline v7, 6 Oct 2026)
//
// The group story and the OM story engine (#1364): the warm paper of the Aalto
// method deck. Colour marks perspectives only; magenta is for friction only;
// every perspective also has its own glyph and hatch, so colour is never the
// only cue. Perspective slots are the colour-blind safe palette Kalle approved
// on 6 Oct 2026. Words on the stage come only from the locale files.
// lib/story/aotTheme.ts reads these values; change them here.
// ---------------------------------------------------------------------------

/**
 * Kalle 20.10.2026: the story follows the new logo, and its colours stay adjustable here for the AD ("Jätä
 * värit säädettäväksi AOT:n puolella, jotta AD voi siellä hio niitä"). Then, the same day: "kirkas sininen
 * vain kehykseen. Hahmot nykyisillä väreillä. Ja tausta oikea vaalean sävy." So the logo's Egyptian blue
 * (#2F5D9B) is the room's frame only, the floor is the logo's marble (#F4F0E7) with the page around it a
 * shade lighter, the wall's hatch and the hairlines are marble greys instead of warm sand, and the figures
 * keep their colours (idle and the six perspective slots, approved 6.10.2026). First values, for the AD.
 */
export const storyWarm = {
  paper: '#f9f7f2',
  floor: '#f4f0e7',
  floorEdge: '#2f5d9b',
  floorEdgeInner: '#b9c6d9',
  /** the wall's hatch and the hairlines on marble (engine keys wallHatch, hair) */
  wallHatch: '#e3ded3',
  hair: '#dcd6ca',
  ink: '#2f2a36',
  ink2: '#6b6272',
  /** emerald: the average and new words */
  accent: '#047857',
  /** friction only */
  friction: '#b8237a',
  idle: '#8a8190',
  /** people outside the round, drawn at outOpacity */
  out: '#b9b0a3',
  outOpacity: 0.42,
  /** perspective slots 1-6: green, lilac, ochre, blue, brick, sand */
  perspectives: [
    { fill: '#4f8a5b', ink: '#2f5d3a' },
    { fill: '#8e86b6', ink: '#5a5282' },
    { fill: '#c4822f', ink: '#8a5615' },
    { fill: '#3e6f9f', ink: '#264a6e' },
    { fill: '#794949', ink: '#4d2c2c' },
    { fill: '#c4bb8d', ink: '#57512f' },
  ],
} as const;

// ---------------------------------------------------------------------------
// Section 6 — Spacing & layout tokens
// Mode-agnostic. Use these instead of raw `max-w-*` / `py-*` to keep page
// rhythm consistent across the site.
// ---------------------------------------------------------------------------

export const space = {
  /** Reading flows, single-column forms (≈448px) */
  containerNarrow: 'mx-auto w-full max-w-md px-5',
  /** Content pages, marketing sections (≈672px) */
  containerDefault: 'mx-auto w-full max-w-2xl px-5',
  /** Host dashboards, lists (≈896px) */
  containerWide: 'mx-auto w-full max-w-4xl px-6',
  /** Full app shell (≈1280px) */
  containerPage: 'mx-auto w-full max-w-7xl px-6',
  /** Vertical rhythm between marketing sections */
  sectionY: 'py-16 md:py-24',
  /** Tighter vertical rhythm for nested sub-sections */
  sectionYTight: 'py-10 md:py-14',
  /** iPhone home-indicator safe area (bottom only) */
  safeAreaBottom: 'pb-[env(safe-area-inset-bottom,0px)]',
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Joins truthy class strings — handy when composing tokens with extras. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** Resolve a typography token for the given mode. */
export function tk(mode: ChalkboardMode, token: TypoToken): string {
  return typo[mode][token];
}
